// One ARKit-52 frame per render frame, from the face graph to the splat's face driver.
// Notes: ~/comments/aos-threejs-poc/useSplatFaceDrive_one_frame_per_frame.md

import { useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import { faceGraphState } from '../../lib/faceGraphState.js'
import { evaluateGraph, updateSMTransitions } from '../../lib/GraphEvaluator.js'
import { faceEvalResult } from '../../lib/faceEvalResult.js'
import { faceClipPlayer } from '../../lib/FaceClipPlayer.js'
import { debugState } from '../../lib/debugState.js'
import { currentBlinkWeight } from '../../lib/blinkRuntime.js'
import { getCurrentArkit } from '../../network/AudioAnimationBuffer.js'
import { composeRigControlArkit, rigControlWeight } from '../../lib/rigControlState.js'
import { faceTrackState, faceTrackWeight } from '../../lib/faceTrackState.js'
import { isFaceAnimEnabled } from '../../lib/faceAnimState.js'
import { composeSplatArkitFrame, ARKIT_LEN } from '../../ogs/pipeline/arkitFrame.ts'
import { composeRig, rigChanged } from '../../ogs/pipeline/faceRigComposer.ts'

/** Fixed frame standing in for the graph's, so a spec can sweep expressions deterministically. */
const forcedFrame = () => {
  const forced = typeof window !== 'undefined' ? window.__OGS_FORCE_ARKIT__ : null
  return forced?.length === ARKIT_LEN ? forced : null
}

/**
 * Drives `face.setArkit` every frame while `active()`.
 *
 * Every input is a GETTER, not a value: the face handle and the drive plan are set
 * asynchronously after mount and nothing re-renders when a ref changes, so a captured
 * value would stay null for the life of the component.
 *
 * @param animationBuffer  the A2F frame buffer; advanced here because no HeadModel is mounted
 * @param getFace          () => `{ setArkit }` or null — the SYNTHETIC path's joint driver
 * @param getRigDrive      () => `{ gather, constraints, push }` or null — the LIVE-RIG path
 * @param getRegions       () => channel groups this bundle declines (scene.json face_drives)
 * @param regionMask       hold the A2F eye/brow/cheek channels at rest during speech
 * @param active           false while the render phase is not `rendering`
 */
export function useSplatFaceDrive({ animationBuffer, getFace, getRigDrive, getRegions, regionMask = true, active }) {
  const arkit = useRef(null)
  if (!arkit.current) arkit.current = new Float32Array(ARKIT_LEN)
  // The rig-vector path's reusable buffers — sized on the first push, never per frame.
  const rig = useRef(null)
  const lastRig = useRef(null)
  const scratch = useRef(null)
  if (!scratch.current) scratch.current = new Float32Array(188)

  useFrame(() => {
    if (!active()) return
    // No HeadModel in splat mode, so nothing else advances the A2F stream.
    animationBuffer?.advancePlayback()

    updateSMTransitions(faceGraphState)
    const { weights, plays } = evaluateGraph(faceGraphState)
    const liveWeight = weights?.['__livelink__'] ?? 0

    // Editor parity: the weight bars poll debugState, and every tick++ lives in the GLB
    // mixer's applyWeights, which never runs here — so without this every face node reads
    // "idle" while it is driving.
    faceEvalResult.liveWeight = liveWeight
    faceEvalResult.weights = weights ?? null
    faceEvalResult.plays = plays ?? null
    debugState.faceWeights = weights ?? {}
    debugState.tick++
    faceClipPlayer.writeTimingsInto(debugState.clipTimings)

    const live = getCurrentArkit()
    const rigWeight = rigControlWeight(weights)
    // Webcam Face Tracker node (ENG-1107): rig controller > track > live, as faceFrame.js states.
    const trackWeight = rigWeight > 0 ? 0 : faceTrackWeight(weights)
    const blinkGate = weights?.['__blink__'] ?? 0
    composeSplatArkitFrame(
      {
        forcedArkit: forcedFrame(),
        rigWeight,
        rigArkit: rigWeight > 0 ? composeRigControlArkit(live) : null,
        trackWeight,
        trackArkit: trackWeight > 0 ? faceTrackState.arkit : null,
        liveWeight,
        liveArkit: live,
        clipWeights: liveWeight > 0 ? null : faceClipPlayer.getBlendedWeights(weights ?? {}, plays),
      },
      {
        regionMask,
        regions: getRegions?.() ?? null,
        blink: blinkGate > 0 ? currentBlinkWeight(blinkGate) : 0,
        faceAnim: isFaceAnimEnabled(),
      },
      arkit.current,
    )
    // ONE of two sinks. The synthetic path poses the part rig's facial joints; the
    // live-rig path maps to a control vector through the constraint stages.
    const face = getFace?.()
    if (face) { face.setArkit(arkit.current); return }

    const drive = getRigDrive?.()
    if (!drive?.gather) return
    const width = drive.gather.length
    if (rig.current?.length !== width) {
      rig.current = new Float32Array(width)
      lastRig.current = null
    }
    composeRig(arkit.current, {
      gather: drive.gather,
      scratch: scratch.current,
      constraints: drive.constraints,
      liveWeight,
      // A tracked frame is AUTHORED like the rig controller's, not A2F speech, so the
      // live-mouth mix must not run on it: fold it into the same gate.
      rigWeight: rigWeight > 0 ? rigWeight : trackWeight,
    }, rig.current)
    // Only on change: a decode costs a frame, and the graph re-evaluates every one.
    if (!rigChanged(rig.current, lastRig.current)) return
    if (lastRig.current?.length !== width) lastRig.current = new Float32Array(width)
    lastRig.current.set(rig.current)
    drive.push(rig.current)
  })

  return arkit
}
